// src/components/PersonalPlacesList.jsx
// Mobile "My Curation" bottom place list sheet
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { STATUS_COLOR, STATUS_LABEL, FOLLOWING_COLORS } from "../constants";

const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";

const PEEK_H = 195;
const FULL_H_VH = 65;

const SORT_OPTIONS = [
  { key: "newest", icon: "arrow_downward" },
  { key: "oldest", icon: "arrow_upward" },
  { key: "collection", icon: "folder" },
];

export default function PersonalPlacesList({
  personalPlaces,
  folders = [],
  followingList,
  selectedFollowingIds,
  followingPlacesMap,
  onPlaceSelect,
  onToggleFollowing,
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState("peek");
  const [sortMode, setSortMode] = useState("newest");
  const listRef = useRef(null);
  const touchY = useRef(0);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [sortMode]);

  const toggle = () => setMode((m) => (m === "peek" ? "full" : "peek"));
  const handleTouchStart = (e) => { touchY.current = e.touches[0].clientY; };
  const handleTouchEnd = (e) => {
    const dy = e.changedTouches[0].clientY - touchY.current;
    if (dy < -40 && mode === "peek") setMode("full");
    if (dy > 40 && mode === "full" && listRef.current?.scrollTop <= 0) setMode("peek");
  };

  // Collect: my places + selected following places
  const allPlaces = [];

  personalPlaces.forEach((p) => allPlaces.push({ ...p, _owner: null, _ownerNickname: null }));

  selectedFollowingIds.forEach((uid) => {
    const u = followingList.find((f) => f.id === uid);
    const places = followingPlacesMap[uid] || [];
    places.forEach((p) => allPlaces.push({ ...p, _owner: uid, _ownerNickname: u?.nickname || "?" }));
  });

  const totalCount = allPlaces.length;

  const timeSort = sortMode === "oldest"
    ? (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
    : (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);

  // ── PlaceRow ──
  const PlaceRow = ({ p }) => {
    const sc = p.status ? STATUS_COLOR[p.status] : null;
    const isFollowing = !!p._owner;
    const followingIdx = isFollowing ? followingList.findIndex((f) => f.id === p._owner) : -1;
    const followingColor = isFollowing ? FOLLOWING_COLORS[followingIdx % FOLLOWING_COLORS.length] : null;

    return (
      <div
        onClick={() => onPlaceSelect(p)}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "8px 6px", borderRadius: 8,
          cursor: "pointer", transition: "background 0.12s",
        }}
      >
        <div style={{
          width: 44, height: 44, borderRadius: 8, flexShrink: 0,
          background: p.photo_url || p.photo_urls?.[0]
            ? `url(${p.photo_urls?.[0] || p.photo_url}) center/cover`
            : "#f4f4f0",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {!p.photo_url && !p.photo_urls?.[0] && (
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#c7c4bf" }}>location_on</span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <p style={{
              margin: 0, fontFamily: FL, fontSize: 16, fontWeight: 600,
              color: "#2f3430", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              flex: 1, minWidth: 0,
            }}>{p.name}</p>
            {isFollowing && (
              <span style={{
                fontFamily: FL, fontSize: 10, fontWeight: 700,
                padding: "2px 6px", borderRadius: 4, flexShrink: 0,
                background: followingColor + "18",
                color: followingColor,
                whiteSpace: "nowrap",
              }}>
                {p._ownerNickname}
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3 }}>
            {sc && (
              <span style={{
                fontFamily: FL, fontSize: 11, fontWeight: 700,
                padding: "1px 6px", borderRadius: 3,
                background: sc.bg, color: sc.color,
              }}>{STATUS_LABEL[p.status]}</span>
            )}
            {p.rating > 0 && (
              <span style={{ fontSize: 11, color: "#c7c4bf" }}>
                {"\u2605".repeat(p.rating)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Sort / group ──
  const renderList = () => {
    if (allPlaces.length === 0) {
      return (
        <p style={{ fontFamily: FL, fontSize: 14, color: "#8a8e8a", margin: "8px 4px", fontStyle: "italic" }}>
          No saved places yet
        </p>
      );
    }

    if (sortMode === "collection") {
      // Group by user-assigned folder (collection)
      const grouped = {};
      allPlaces.forEach((p) => {
        const key = p.folder_id ? String(p.folder_id) : "_none";
        (grouped[key] = grouped[key] || []).push(p);
      });

      // Order: folders in their existing order, then uncollected at end
      const orderedKeys = folders
        .map((f) => String(f.id))
        .filter((k) => grouped[k]);
      if (grouped["_none"]) orderedKeys.push("_none");

      return orderedKeys.map((key) => {
        const folder = key !== "_none" ? folders.find((f) => String(f.id) === key) : null;
        const color = folder?.color || "#8a8e8a";
        const label = folder?.name || "Uncollected";
        const items = grouped[key].sort(timeSort);
        return (
          <div key={key} style={{ marginBottom: 8 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 6px 6px",
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: "50%",
                background: color, flexShrink: 0,
              }} />
              <span style={{
                fontFamily: FL, fontSize: 14, fontWeight: 700, color,
              }}>{label}</span>
              <span style={{ fontFamily: FL, fontSize: 13, fontWeight: 500, color: "#8a8e8a" }}>
                {items.length}
              </span>
            </div>
            {items.map((p) => <PlaceRow key={`${p.id}_${p._owner || "me"}`} p={p} />)}
          </div>
        );
      });
    }

    // newest / oldest — group by person when following users selected
    if (selectedFollowingIds.length > 0) {
      const myPlaces = allPlaces.filter((p) => !p._owner).sort(timeSort);
      const sections = [];

      if (myPlaces.length > 0) {
        sections.push({ key: "me", nickname: "Me", color: "#655d54", avatar: null, places: myPlaces });
      }

      selectedFollowingIds.forEach((uid) => {
        const u = followingList.find((f) => f.id === uid);
        const idx = followingList.findIndex((f) => f.id === uid);
        const color = FOLLOWING_COLORS[idx % FOLLOWING_COLORS.length];
        const userPlaces = allPlaces.filter((p) => p._owner === uid).sort(timeSort);
        if (userPlaces.length > 0) {
          sections.push({ key: uid, nickname: u?.nickname || "?", color, avatar: u?.nickname?.[0]?.toUpperCase(), places: userPlaces });
        }
      });

      return sections.map((sec, si) => (
        <div key={sec.key}>
          {si > 0 && <div style={{ height: 1, background: "#f0f0ec", margin: "6px 6px" }} />}
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 6px 6px",
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: "50%",
              background: sec.key === "me" ? "linear-gradient(135deg,#595149,#655d54)" : sec.color,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: FH, fontStyle: "italic", fontSize: 12, color: "#fff", fontWeight: 700,
            }}>
              {sec.key === "me" ? "Me" : sec.avatar}
            </div>
            <span style={{ fontFamily: FL, fontSize: 15, fontWeight: 700, color: "#2f3430" }}>
              {sec.nickname}
            </span>
            <span style={{ fontFamily: FL, fontSize: 13, fontWeight: 500, color: "#8a8e8a" }}>
              {sec.places.length}
            </span>
          </div>
          {sec.places.map((p) => <PlaceRow key={`${p.id}_${p._owner || "me"}`} p={p} />)}
        </div>
      ));
    }

    // My places only — flat list
    return allPlaces.sort(timeSort).map((p) => <PlaceRow key={p.id} p={p} />);
  };

  const height = mode === "full" ? `${FULL_H_VH}vh` : PEEK_H;

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        position: "fixed",
        left: 0, right: 0, bottom: 64,
        zIndex: 30,
        height,
        background: "#fff",
        borderRadius: "18px 18px 0 0",
        boxShadow: "0 -4px 20px rgba(0,0,0,0.08)",
        transition: "height 0.35s cubic-bezier(0.16,1,0.3,1)",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Drag handle */}
      <div onClick={toggle} style={{
        display: "flex", justifyContent: "center",
        padding: "10px 0 6px", flexShrink: 0, cursor: "grab",
      }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: "#d4d1c7" }} />
      </div>

      {/* Header: title + sort buttons */}
      <div style={{
        padding: "0 20px 6px", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <p style={{
          fontFamily: FH, fontStyle: "italic", fontSize: 18, fontWeight: 700,
          color: "#1c1c1e", margin: 0,
        }}>
          Places
          <span style={{ fontSize: 15, fontWeight: 500, color: "#8a8e8a", marginLeft: 6 }}>
            {totalCount}
          </span>
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          {SORT_OPTIONS.map((s) => (
            <button
              key={s.key}
              title={s.key}
              onClick={() => setSortMode(s.key)}
              style={{
                background: sortMode === s.key ? "#ede0d5" : "transparent",
                border: "none", borderRadius: 5, padding: "4px 5px",
                cursor: "pointer", display: "flex", alignItems: "center",
                transition: "background 0.15s",
              }}
            >
              <span className="material-symbols-outlined" style={{
                fontSize: 17,
                color: sortMode === s.key ? "#655d54" : "#8a8e8a",
                fontVariationSettings: "'FILL' 0, 'wght' 300",
              }}>{s.icon}</span>
            </button>
          ))}
          <span
            className="material-symbols-outlined"
            onClick={toggle}
            style={{ fontSize: 20, color: "#8a8e8a", cursor: "pointer", marginLeft: 4 }}
          >
            {mode === "full" ? "expand_more" : "expand_less"}
          </span>
        </div>
      </div>

      {/* List */}
      <div ref={listRef} style={{
        flex: 1, overflowY: "auto", padding: "0 16px 16px", minHeight: 0,
      }}>
        {renderList()}
      </div>
    </div>
  );
}
